import { getApiUrl } from '../config/api';

export interface WaitlistEntry {
  id: string;
  email: string;
  source: string;
  createdAt: string;
  isConverted: boolean;
  convertedAt?: string;
  country?: string;
  region?: string;
  city?: string;
  timezone?: string;
  ipAddress?: string;
}

export interface WaitlistAnalytics {
  totalSignups: number;
  dailySignups: Array<{
    date: string;
    count: number;
  }>;
  sourceBreakdown: Array<{
    source: string;
    count: number;
    percentage: number;
  }>;
  locationBreakdown: {
    countries: { [key: string]: number };
    regions: { [key: string]: number };
    cities: { [key: string]: number };
    timezones: { [key: string]: number };
  };
  conversionRate: number;
  growthRate: number;
}

export interface DashboardStats {
  totalUsers: number;
  totalProjects: number;
  activeUsers: number;
  systemHealth: {
    database: string;
    storage: string;
    services: string;
  };
  recentActivity: {
    newUsers: number;
    newProjects: number;
    activeProjects: number;
  };
}

class AdminService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  // Dashboard APIs
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await fetch(getApiUrl('/admin/dashboard'), {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  async getSystemHealth() {
    try {
      const response = await fetch(getApiUrl('/admin/system-health'), {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch system health: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching system health:', error);
      throw error;
    }
  }

  // Waitlist APIs
  async getWaitlistEntries(page: number = 1, limit: number = 50): Promise<{
    data: WaitlistEntry[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    try {
      const response = await fetch(getApiUrl(`/admin/waitlist?page=${page}&limit=${limit}`), {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch waitlist entries: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching waitlist entries:', error);
      throw error;
    }
  }

  async getWaitlistAnalytics(): Promise<WaitlistAnalytics> {
    try {
      const response = await fetch(getApiUrl('/admin/waitlist/analytics'), {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch waitlist analytics: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching waitlist analytics:', error);
      throw error;
    }
  }

  async exportWaitlist(): Promise<Blob> {
    try {
      const response = await fetch(getApiUrl('/admin/waitlist/export'), {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to export waitlist: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error exporting waitlist:', error);
      throw error;
    }
  }

  async removeWaitlistEntry(id: string): Promise<void> {
    try {
      const response = await fetch(getApiUrl(`/admin/waitlist/${id}`), {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to remove waitlist entry: ${response.status}`);
      }
    } catch (error) {
      console.error('Error removing waitlist entry:', error);
      throw error;
    }
  }

  async markAsConverted(id: string): Promise<void> {
    try {
      const response = await fetch(getApiUrl(`/admin/waitlist/${id}/convert`), {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to mark as converted: ${response.status}`);
      }
    } catch (error) {
      console.error('Error marking as converted:', error);
      throw error;
    }
  }
}

export const adminService = new AdminService();
export default adminService;
